import { useEffect, useMemo, useState } from 'react'
import { ApiClientError } from '../../services/apiClient'
import { Modal } from '../../components/Modal'
import {
  RENEWAL_WARNING_DAYS,
  daysUntil,
  describeChangeType,
  formatMxn,
  suscripcionClient,
  type BillingType,
  type ChangeType,
  type Plan,
  type SuscripcionState,
} from '../../services/suscripcionClient'

/**
 * Portal del usuario — Suscripción §10 SRS.
 *
 *   §10.1: estado actual (plan, fechas, downgrade pendiente).
 *   §10.3: catálogo de planes activos+visibles.
 *   §10.4: flujo de 5 pasos. Aquí pasos 1 (selección) y 2 (confirmación).
 *          Paso 3 y 4 los hace el backend en /suscripcion/checkout. Tras éxito,
 *          mostramos la referencia ITR + redirigimos al "portal del banco" — en este
 *          milestone simulamos el redirect con un aviso al usuario (no hay portal real
 *          conectado todavía; en producción n8n entregará la URL del portal del banco
 *          mediante un payload alternativo que se incorporará luego).
 *
 *   §10.5: lógica upgrade/downgrade/renovación se calcula en backend; aquí sólo
 *          mostramos el resultado.
 */
export function SuscripcionPage() {
  const [state, setState] = useState<SuscripcionState | null>(null)
  const [plans, setPlans] = useState<Plan[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [billingType, setBillingType] = useState<BillingType>('monthly')
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [cancelingDowngrade, setCancelingDowngrade] = useState(false)
  const [flashMessage, setFlashMessage] = useState<string | null>(null)

  function reload() {
    setLoading(true)
    Promise.all([suscripcionClient.show(), suscripcionClient.planes()])
      .then(([s, p]) => {
        setState(s)
        setPlans(p.items)
        if (s.subscription) setBillingType(s.subscription.billing_type)
      })
      .catch((err) =>
        setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo cargar.'),
      )
      .finally(() => setLoading(false))
  }
  useEffect(() => { reload() }, [])

  const currentPlanId = state?.subscription?.plan.id ?? null

  function openConfirm(plan: Plan) {
    setSelectedPlan(plan)
    setError(null)
    setConfirmOpen(true)
  }

  async function handleCheckout() {
    if (!selectedPlan) return
    setSubmitting(true)
    setError(null)
    try {
      const res = await suscripcionClient.checkout(selectedPlan.id, billingType)
      setConfirmOpen(false)
      if (res.change_type === 'downgrade') {
        setFlashMessage(`Cambio a ${res.new_plan.name} programado para el ${formatDate(res.effective_at)}.`)
        reload()
      } else {
        window.location.href = res.payment_url
      }
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo iniciar el pago.')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleCancelDowngrade() {
    setCancelingDowngrade(true)
    try {
      const { canceled } = await suscripcionClient.cancelDowngrade()
      if (canceled) {
        setFlashMessage('Downgrade programado cancelado.')
        setTimeout(() => setFlashMessage(null), 3500)
      }
      reload()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo cancelar.')
    } finally {
      setCancelingDowngrade(false)
    }
  }

  const sub = state?.subscription
  const nextPlan = state?.next_plan
  const daysLeft = sub ? daysUntil(sub.current_period_end) : null
  const renewalDue = daysLeft !== null && daysLeft <= RENEWAL_WARNING_DAYS
  const currentFullPlan = plans.find(p => p.id === currentPlanId) ?? null

  return (
    <div className="content-stack">
      <header className="list-toolbar">
        <div className="list-toolbar__title">
          <h1>Suscripción</h1>
          <span className="muted">Gestiona tu plan y métodos de pago.</span>
        </div>
      </header>

      {error && <p className="error">{error}</p>}
      {flashMessage && <p className="info">{flashMessage}</p>}

      {loading && <p className="muted center">Cargando…</p>}

      {!loading && (
        <>
          {/* §10.1 — Estado del plan actual */}
          <section className="card">
            <h3>Plan actual</h3>
            {!sub ? (
              <p className="muted">Aún no has contratado ningún plan.</p>
            ) : (
              <div className="content-stack" style={{ gap: '.6rem' }}>
                <p>
                  <strong>{sub.plan.name}</strong>{' '}
                  <span className="badge badge--pdf_generated">{sub.status}</span>{' '}
                  <span className="muted">({sub.billing_type === 'yearly' ? 'anual' : 'mensual'})</span>
                </p>
                {sub.plan.description && <p className="muted">{sub.plan.description}</p>}
                {(sub.plan.monthly_price > 0 || sub.plan.yearly_price > 0) && (
                  <p className="muted">
                    Inicio: {formatDate(sub.start_date)} · Vence: {formatDate(sub.current_period_end)}
                  </p>
                )}

                {renewalDue && currentFullPlan && (
                  <div>
                    <button type="button" className="primary-btn small" onClick={() => openConfirm(currentFullPlan)}>
                      Renovar
                    </button>
                  </div>
                )}

                <PlanLimitsSummary plan={sub.plan} />

                {nextPlan && (
                  <div className="alert alert--info" style={{ marginTop: '.8rem' }}>
                    <strong>Downgrade pendiente:</strong> al inicio del próximo ciclo
                    tu plan cambiará a <strong>{nextPlan.name}</strong>.{' '}
                    <button
                      type="button"
                      className="ghost-btn small"
                      onClick={handleCancelDowngrade}
                      disabled={cancelingDowngrade}
                    >
                      {cancelingDowngrade ? 'Cancelando…' : 'Mantener mi plan actual'}
                    </button>
                  </div>
                )}

                {state.pending_payment && (
                  <div className="alert alert--warning" style={{ marginTop: '.8rem' }}>
                    Hay un pago en curso (<span className="mono">{state.pending_payment.reference}</span>)
                    por {formatMxn(state.pending_payment.amount)}. Si ya completaste el pago,
                    la activación llegará automáticamente cuando el banco confirme.
                  </div>
                )}
              </div>
            )}
          </section>

          {/* §10.3 — Catálogo de planes */}
          <section className="card">
            <header className="section-head">
              <h3>Planes disponibles</h3>
              <div className="billing-toggle" role="tablist" aria-label="Periodo de facturación">
                <button
                  type="button"
                  className={billingType === 'monthly' ? 'tab-button active' : 'tab-button'}
                  onClick={() => setBillingType('monthly')}
                  aria-pressed={billingType === 'monthly'}
                >
                  Mensual
                </button>
                <button
                  type="button"
                  className={billingType === 'yearly' ? 'tab-button active' : 'tab-button'}
                  onClick={() => setBillingType('yearly')}
                  aria-pressed={billingType === 'yearly'}
                >
                  Anual
                </button>
              </div>
            </header>

            {plans.length === 0 ? (
              <p className="muted">No hay planes disponibles en este momento.</p>
            ) : (
              <ul className="plans-grid">
                {plans.map((p) => (
                  <PlanCard
                    key={p.id}
                    plan={p}
                    billingType={billingType}
                    isCurrent={currentPlanId === p.id && sub?.billing_type === billingType}
                    isRenewable={currentPlanId === p.id && sub?.billing_type === billingType && renewalDue}
                    onSelect={() => openConfirm(p)}
                  />
                ))}
              </ul>
            )}
          </section>
        </>
      )}

      {/* §10.4 paso 2 — modal de confirmación */}
      {selectedPlan && (
        <ConfirmCheckoutModal
          open={confirmOpen}
          onClose={() => !submitting && setConfirmOpen(false)}
          plan={selectedPlan}
          billingType={billingType}
          currentSubscription={sub ?? null}
          submitting={submitting}
          error={error}
          onConfirm={handleCheckout}
        />
      )}

    </div>
  )
}

// ─── Componentes locales ────────────────────────────────────────────────────

function PlanLimitsSummary({ plan }: { plan: { max_reports_per_month: number; max_ai_enhancements_per_month: number; max_clients: number; ai_enhancement_enabled: boolean } }) {
  return (
    <ul className="muted" style={{ marginTop: '.4rem', paddingLeft: '1.2rem' }}>
      <li>Reportes mensuales: {fmtLimit(plan.max_reports_per_month)}</li>
      <li>
        Mejoras con IA mensuales:{' '}
        {plan.ai_enhancement_enabled ? fmtLimit(plan.max_ai_enhancements_per_month) : 'No incluido'}
      </li>
      <li>Clientes: {fmtLimit(plan.max_clients)}</li>
    </ul>
  )
}

function PlanCard({
  plan, billingType, isCurrent, isRenewable, onSelect,
}: {
  plan: Plan
  billingType: BillingType
  isCurrent: boolean
  isRenewable: boolean
  onSelect: () => void
}) {
  const price = billingType === 'yearly' ? plan.yearly_price : plan.monthly_price
  const priceLabel = price > 0 ? formatMxn(price) : 'Sin precio configurado'

  return (
    <li className={`plan-card${plan.recommended ? ' plan-card--recommended' : ''}`}>
      {plan.recommended && <span className="badge badge--pdf_generated">Recomendado</span>}
      <h4>{plan.name}</h4>
      {plan.description && <p className="muted">{plan.description}</p>}

      <p className="plan-card__price">
        <strong>{priceLabel}</strong>{' '}
        <span className="muted">/ {billingType === 'yearly' ? 'año' : 'mes'}</span>
      </p>

      <PlanLimitsSummary plan={plan} />

      {plan.features.length > 0 && (
        <ul className="plan-card__features">
          {plan.features.map((f, i) => (
            <li key={i}>{f}</li>
          ))}
        </ul>
      )}

      <button
        type="button"
        className={isCurrent && !isRenewable ? 'ghost-btn' : 'primary-btn'}
        onClick={onSelect}
        disabled={(isCurrent && !isRenewable) || price <= 0}
      >
        {isRenewable ? 'Renovar' : isCurrent ? 'Plan actual' : 'Cambiar a este plan'}
      </button>
    </li>
  )
}

function ConfirmCheckoutModal({
  open, onClose, plan, billingType, currentSubscription, submitting, error, onConfirm,
}: {
  open: boolean
  onClose: () => void
  plan: Plan
  billingType: BillingType
  currentSubscription: SuscripcionState['subscription']
  submitting: boolean
  error: string | null
  onConfirm: () => void
}) {
  const price = billingType === 'yearly' ? plan.yearly_price : plan.monthly_price

  const inferredChange: ChangeType = useMemo(() => {
    const currentIsFreePlaceholder =
      currentSubscription &&
      currentSubscription.plan.monthly_price === 0 &&
      currentSubscription.plan.yearly_price === 0
    if (!currentSubscription || currentSubscription.status === 'canceled' || currentIsFreePlaceholder) return 'new'
    const samePlan = currentSubscription.plan.id === plan.id
    const sameBilling = currentSubscription.billing_type === billingType
    if (samePlan && sameBilling) return 'renewal'
    const currentRate = dailyRate(
      currentSubscription.billing_type === 'yearly'
        ? currentSubscription.plan.yearly_price
        : currentSubscription.plan.monthly_price,
      currentSubscription.billing_type,
    )
    const newRate = dailyRate(price, billingType)
    return newRate > currentRate ? 'upgrade' : newRate < currentRate ? 'downgrade' : 'renewal'
  }, [currentSubscription, plan, billingType, price])

  // Prorrateo estimado — misma fórmula que el backend, con cap al periodo para evitar montos absurdos.
  const prorataEstimate: number | null = useMemo(() => {
    if (inferredChange !== 'upgrade' || !currentSubscription) return null
    const periodDays = billingType === 'yearly' ? 360 : 30
    const end = new Date(currentSubscription.current_period_end.replace(' ', 'T'))
    const daysRemaining = Math.max(0, Math.min(periodDays, Math.floor((end.getTime() - Date.now()) / 86400000)))
    const currentRate = dailyRate(
      currentSubscription.billing_type === 'yearly'
        ? currentSubscription.plan.yearly_price
        : currentSubscription.plan.monthly_price,
      currentSubscription.billing_type,
    )
    const newRate = dailyRate(price, billingType)
    return Math.max(0, Math.round((newRate - currentRate) * daysRemaining * 100) / 100)
  }, [inferredChange, currentSubscription, billingType, price])

  // Fechas estimadas del nuevo ciclo.
  const periodDates = useMemo(() => {
    const periodDays = billingType === 'yearly' ? 360 : 30
    const now = new Date()
    if (inferredChange === 'upgrade' && currentSubscription) {
      // Upgrade: inicia hoy, vence en la misma fecha que el ciclo actual.
      const end = new Date(currentSubscription.current_period_end.replace(' ', 'T'))
      return { start: now, end }
    }
    if (inferredChange === 'renewal' && currentSubscription) {
      // Renewal: extiende desde el fin actual, salvo que ya haya vencido, en cuyo caso inicia hoy.
      const currentEnd = new Date(currentSubscription.current_period_end.replace(' ', 'T'))
      const base = now.getTime() > currentEnd.getTime() ? now : currentEnd
      const end = new Date(base.getTime() + periodDays * 86400000)
      return { start: base, end }
    }
    // New: inicia hoy.
    const end = new Date(now.getTime() + periodDays * 86400000)
    return { start: now, end }
  }, [inferredChange, currentSubscription, billingType])

  const isPayment = inferredChange !== 'downgrade'

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Confirma el cambio de plan"
      description={describeChangeType(inferredChange)}
      size="md"
      dismissible={!submitting}
      footer={
        <div className="modal__actions">
          <button type="button" className="ghost-btn" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button type="button" className="primary-btn" onClick={onConfirm} disabled={submitting}>
            {submitting
              ? 'Procesando…'
              : inferredChange === 'downgrade'
                ? 'Programar cambio'
                : 'Proceder al pago'}
          </button>
        </div>
      }
    >
      <div className="content-stack" style={{ gap: '.6rem' }}>
        <p>
          Plan seleccionado: <strong>{plan.name}</strong>{' '}
          <span className="muted">({billingType === 'yearly' ? 'anual' : 'mensual'})</span>
        </p>
        {plan.description && <p className="muted">{plan.description}</p>}

        <dl className="kv">
          <dt>Precio del plan</dt>
          <dd>{formatMxn(price)}</dd>

          {inferredChange === 'upgrade' && (
            <>
              <dt>Cobro hoy</dt>
              <dd>{prorataEstimate !== null ? formatMxn(prorataEstimate) : '—'}{' '}
                <span className="muted">(prorrateo por días restantes del ciclo)</span>
              </dd>
            </>
          )}

          {inferredChange === 'downgrade' && (
            <>
              <dt>Cobro</dt>
              <dd>Sin cobro — el cambio se aplica al iniciar el próximo ciclo.</dd>
            </>
          )}

          {inferredChange === 'renewal' && (
            <>
              <dt>Cobro</dt>
              <dd>{formatMxn(price)} <span className="muted">(extiende tu periodo actual)</span></dd>
            </>
          )}

          {inferredChange !== 'downgrade' && (
            <>
              <dt>Inicio del ciclo</dt>
              <dd>{formatDate(periodDates.start.toISOString())}</dd>
              <dt>Fin del ciclo</dt>
              <dd>{formatDate(periodDates.end.toISOString())}</dd>
            </>
          )}

          {inferredChange === 'downgrade' && currentSubscription && (
            <>
              <dt>Efectivo a partir de</dt>
              <dd>{formatDate(currentSubscription.current_period_end)}</dd>
            </>
          )}
        </dl>

        {isPayment && (
          <p className="muted" style={{ fontSize: '.85rem' }}>
            Al continuar serás redirigido al portal de pago del banco para completar la transacción de forma segura.
          </p>
        )}

        {error && <p className="error">{error}</p>}
      </div>
    </Modal>
  )
}


// ─── Utilidades locales ─────────────────────────────────────────────────────

function fmtLimit(v: number): string {
  if (v === -1) return 'Ilimitado'
  if (v === 0) return 'No incluido'
  return String(v)
}

function dailyRate(price: number, billing: BillingType): number {
  if (price <= 0) return 0
  return price / (billing === 'yearly' ? 360 : 30)
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value.replace(' ', 'T'))
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' })
}
